/**
 * An organization's workspaces — what a member's install joins, and what it
 * pulls from then on.
 *
 * Just the list. Opening one *replaces the rail* with that workspace's own, the
 * way opening a repository on GitHub replaces its owner's navigation, so there
 * is nothing here but the rows and the one verb that makes another.
 *
 * And no search box. An organization has tens of workspaces, not thousands —
 * the list is one screen and the browser's own find already works on it — so a
 * field whose whole job was to hide rows was a control that made the page look
 * bigger than it is. What is left is the one filter that hides something on
 * purpose: the archived ones, which are not gone, only put away.
 *
 * The last column before the chevron is the one thing that differs between the
 * two people who read this page. An administrator is shown **who published**,
 * because what they are deciding is whether a workspace is looked after.
 * Somebody holding a seat is shown **what gives them access**, because what
 * they are deciding is whether they may touch it. Neither is the other's
 * column with a word removed.
 *
 * The page does not wait for the seats to be paid for. Before any are bought,
 * or after they lapse, a member still prepares and edits workspaces here; what
 * waits is Airlock on a machine, which joins and pulls them only once the
 * organization pays — and the page says so above the list rather than hiding
 * it.
 */
import { useEffect, useState } from 'react';
import type React from 'react';
import { color } from '../../theme/tokens';
import { HeaderButton } from '../../components/Shell';
import { List, ListEmpty, ListMeta, ListName, ListRow, ListTag, Note, PageBar } from '../../components/list';
import { api, apiPath } from '../../lib/api';
import type { Organization, Seat, WorkspaceList, WorkspaceRight, WorkspaceSummary } from '../../lib/types';
import { actorLabel, failureText } from './shared';
import { sentenceList } from './parts';
import { since } from '../../lib/format';
import { PageError, SkeletonRows, useLoadPhase } from '../../components/Loading';

/** Workspace · Version · Projects · Last published (or Access), and the chevron's column (§40.7). */
const COLUMNS = 'minmax(0,1.4fr) 80px 110px minmax(0,1fr)';

export function Workspaces({
  organization,
  admin,
  onOpen
}: {
  organization: Organization;
  /** Whether this account administers this organization. */
  admin: boolean;
  onOpen: (workspaceId: string | null, right: WorkspaceRight | null, name: string | null) => void;
}): React.JSX.Element {
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archived, setArchived] = useState(false);
  // Only the lapse is said out loud. An organization that has bought nothing
  // yet is not in a broken state — workspaces are prepared and edited here
  // before any seat exists — and a warning above every list was telling a new
  // organization something was wrong with it on the day it opened.
  const lapsed = organization.seatAccess === 'lapsed';

  // Bumped by the page error's « Try again » (#120), which reads the list afresh.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setWorkspaces(null);
    setError(null);
    api
      .get<WorkspaceList>(apiPath`/orgs/${organization.id}/workspaces`)
      .then((answer) => setWorkspaces(answer.workspaces))
      .catch((failure: unknown) => setError(failureText(failure, 'The workspaces could not be read.')));
  }, [organization.id, attempt]);

  // A member without a seat is told who gives seats, by name (54e) — the
  // organization's seat list, read as the first day reads it. A list that
  // will not load leaves the sentence naming the roles instead.
  const seatless = organization.seatStatus === 'member' && !admin;
  const [givers, setGivers] = useState<string[]>([]);
  useEffect(() => {
    if (!seatless) return;
    let live = true;
    void api
      .get<{ seats: Seat[] }>(apiPath`/orgs/${organization.id}/seats`)
      .then((answer) => {
        if (!live) return;
        setGivers(answer.seats.filter((seat) => seat.role === 'owner' || seat.role === 'admin').map((seat) => seat.email));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [organization.id, seatless]);

  // Nothing for 300 ms, then the skeleton (§33) rather than « Reading … ».
  const phase = useLoadPhase(workspaces === null && !error);

  const all = workspaces ?? [];
  const shown = all.filter((workspace) => archived || !workspace.archivedAt);
  const hidden = all.filter((workspace) => workspace.archivedAt).length;

  return (
    <>
      {(admin || hidden > 0) && (
        <PageBar>
          {/* A display toggle precedes the page's primary, at its left
              (§40.9); its label says what it will do. */}
          {hidden > 0 && (
            <HeaderButton pressed={archived} onClick={() => setArchived((on) => !on)}>
              {archived ? 'Hide archived' : 'Show archived'}
            </HeaderButton>
          )}
          {/* Starting one is the organization's act — a member publishes a
              workspace from their own Airlock, which is where they made it. */}
          {admin && (
            <HeaderButton primary onClick={() => onOpen(null, 'admin', null)}>
              New workspace
            </HeaderButton>
          )}
        </PageBar>
      )}

      {lapsed && (
        <div style={{ padding: '16px var(--al-gutter) 0' }}>
          <Note tone="warn">
            {`${organization.name}’s seats have lapsed. Its workspaces can still be read and edited here; machines stop joining and pulling them until the subscription is settled, and pick up where they left off when it is.`}
          </Note>
        </div>
      )}

      {workspaces === null ? (
        error ? (
          <div style={{ padding: '0 var(--al-gutter)' }}>
            <PageError onRetry={() => setAttempt((n) => n + 1)} />
          </div>
        ) : (
          phase === 'skeleton' && (
            <div style={{ padding: '0 12px' }}>
              <SkeletonRows columns={COLUMNS} />
            </div>
          )
        )
      ) : shown.length === 0 ? (
        seatless ? (
          // A member without a seat is shown nothing yet, and told why (proposal 14).
          <ListEmpty title="No workspace for you yet">
            Workspaces shared with your teams appear here once you hold a seat in {organization.name}.{' '}
            {givers.length
              ? `${sentenceList(givers.slice(0, 2), 'or')} gives seats.`
              : 'Its owner or an administrator gives seats.'}
          </ListEmpty>
        ) : (
          <ListEmpty title="No workspaces yet">Publish one from Airlock and it appears here.</ListEmpty>
        )
      ) : (
        <List columns={COLUMNS} header={['Workspace', 'Version', 'Projects', admin ? 'Last published' : 'Access']}>
          {shown.map((workspace) => {
            const open = (): void => onOpen(workspace.id, workspace.access.right, workspace.name);
            return (
              <ListRow key={workspace.id} columns={COLUMNS} onOpen={open}>
                <ListName
                  name={workspace.name}
                  swatch={workspace.color}
                  href={`/orgs/${encodeURIComponent(organization.slug)}/workspaces/${encodeURIComponent(workspace.id)}/projects`}
                  onOpen={open}
                >
                  {workspace.archivedAt && <ListTag title="Members' copies have unlinked and kept their clone">ARCHIVED</ListTag>}
                </ListName>
                <ListMeta mono>{`v${workspace.version}`}</ListMeta>
                <ListMeta>{`${workspace.projects} ${workspace.projects === 1 ? 'project' : 'projects'}`}</ListMeta>
                {/* The one column that differs between the two people reading
                    this page. An administrator is deciding whether a workspace
                    is looked after, so it says who published it. Somebody
                    holding a seat is deciding whether they may touch one, so it
                    says what gives them the right — which is a fact about their
                    team, not about the workspace. */}
                <ListMeta>
                  {admin ? (
                    <>
                      {actorLabel(workspace.updatedBy)} · {since(workspace.updatedAt)}
                    </>
                  ) : (
                    <>
                      {workspace.access.right} · {workspace.access.via}
                    </>
                  )}
                </ListMeta>
              </ListRow>
            );
          })}
        </List>
      )}

      {shown.length > 0 && (
        <p style={{ margin: 0, padding: '18px var(--al-gutter) 30px', maxWidth: 640, fontSize: 12, lineHeight: 1.6, color: color.inkFaint }}>
          A workspace is what a machine works in, with everything belonging to that machine taken out: its projects and
          how to run them, the configuration, the API console and which ticket boards feed it. Workflows are shared by
          the organization, on its own rail — one list, in every workspace.
        </p>
      )}
    </>
  );
}
